import moment from "moment";
import Router from "next/router";
import Slider from "rc-slider";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { SearchQuery } from "shared/data/reducers/types/storage";
import { hourMinuteFormat } from "shared/lib/datetime";
import { rangeNumToTime } from "shared/lib/functions";

import { FilterRangeDescription } from "@components/Filters/FilterRangeDescription";

import { ROUTES } from "@shared/constants";

import FilterBoxTabs, { Props as FilterBoxTabsProps } from "./FilterBoxTabs";
import { MAX_HOURS, defaultRange, defaultTimeRangeLabel } from "./constants";
import {
  getFlightType,
  getNumberOfFlights,
  hoursToDuration,
  rangeNumToDuration,
} from "./helpers";
import { State } from "./types";

const { createSliderWithTooltip } = Slider;
const Range = createSliderWithTooltip(Slider.Range);

type Props = {
  timeFilters: State["timeFilters"];
  query: SearchQuery;
  setState: React.Dispatch<React.SetStateAction<State>>;
  timeCelling: number;
} & Omit<FilterBoxTabsProps, "numberOfFlights" | "flightType">;

const FilterBox2 = ({
  activeFilter,
  timeFilters,
  query,
  setState,
  timeCelling,
}: Props): React.ReactElement => {
  const { defaultCountry } = useSelector((state: AppState) => ({
    defaultCountry:
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      "",
  }));
  const flightType = getFlightType(query);
  const numberOfFlights = getNumberOfFlights(query);

  const previousActiveFilterRef = useRef(activeFilter);
  const isTabSwitchRef = useRef(false);

  useEffect(() => {
    if (previousActiveFilterRef.current !== activeFilter) {
      isTabSwitchRef.current = true;
      previousActiveFilterRef.current = activeFilter;

      setTimeout(() => {
        isTabSwitchRef.current = false;
      }, 100);
    }
  }, [activeFilter]);

  // round to nearest up 0,5
  const timeCellingHours =
    timeCelling === 0
      ? MAX_HOURS
      : Number((Math.ceil((timeCelling / 60) * 2) / 2).toFixed(2));

  const setFilterTime = (time: number, type: string) => {
    setState((state) => ({
      ...state,
      timeFilters: {
        ...timeFilters,
        [activeFilter]: {
          ...timeFilters[activeFilter],
          [type]: time,
        },
      },
    }));
  };

  const setFilterTimeQuery = (time: any, type: string): void => {
    if (isTabSwitchRef.current) {
      return;
    }

    const queryParamName = `filter_${activeFilter}_${type}`;

    const currentMin = query[`${queryParamName}_min`];
    const currentMax = query[`${queryParamName}_max`];
    if (
      currentMin !== undefined &&
      currentMax !== undefined &&
      Number(currentMin) === time[0] &&
      Number(currentMax) === time[1]
    ) {
      return;
    }

    const param = {
      [`${queryParamName}_min`]: time[0],
      [`${queryParamName}_max`]: time[1],
    };

    if (type === "departure") {
      Router.replace(
        {
          pathname: ROUTES.RESULTS,
          query: {
            ...query,
            ...param,
            date: Date.now(),
          },
        },
        undefined,
        { shallow: false, scroll: false }
      );
    } else {
      Router.replace(
        {
          pathname: ROUTES.RESULTS,
          query: {
            ...query,
            ...param,
          },
        },
        undefined,
        { shallow: true, scroll: false }
      );
    }
  };

  const getValue = (key: string) => {
    return timeFilters[activeFilter]?.[key] !== undefined
      ? timeFilters[activeFilter][key]
      : key === "journeyDuration"
      ? [0, timeCellingHours]
      : [0, MAX_HOURS];
  };

  const labelFormat = (label: string, format: string) => {
    const separator = " - ";
    const initFormat = "HH:mm";
    return label
      .split(separator)
      .map((timeStr) => moment(timeStr, initFormat).format(format))
      .join(separator);
  };

  const getLabel = (
    key: string,
    numberToStringFn: any,
    defaultLabel: string
  ) => {
    return timeFilters[activeFilter] !== undefined
      ? timeFilters[activeFilter][key] !== undefined
        ? `${numberToStringFn(
            timeFilters[activeFilter][key][0]
          )} - ${numberToStringFn(timeFilters[activeFilter][key][1])}`
        : defaultLabel
      : defaultLabel;
  };

  // Departure time
  const departureLabel = getLabel(
    "departure",
    rangeNumToTime,
    defaultTimeRangeLabel
  );
  const departureValue = getValue("departure");

  // Arrival time
  const arrivalLabel = getLabel(
    "arrival",
    rangeNumToTime,
    defaultTimeRangeLabel
  );
  const arrivalValue = getValue("arrival");

  // Transfer duration
  const transferLabel = getLabel("transfer", hoursToDuration, defaultRange);
  const transferValue = getValue("transfer");

  const jurneyDurationLabel = getLabel(
    "journeyDuration",
    hoursToDuration,
    `${0}h - ${timeCellingHours}h`
  );
  const journeyDurationValue = getValue("journeyDuration");
  return (
    <div>
      <div className="filters-box" id="time-filters">
        <FilterBoxTabs
          setState={setState}
          activeFilter={activeFilter}
          flightType={flightType}
          numberOfFlights={numberOfFlights}
        />

        <div className="filter-range-box filter-tab-active">
          <FilterRangeDescription
            translationId="Filters.departureTime"
            rangeId={`departure-time-label-${activeFilter}`}
          >
            {labelFormat(departureLabel, hourMinuteFormat(defaultCountry))}
          </FilterRangeDescription>

          <Range
            testId="departure_filter"
            tipFormatter={(value) =>
              moment(rangeNumToTime(value), "HH:mm").format(
                hourMinuteFormat(defaultCountry)
              )
            }
            onChange={(e) => setFilterTime(e, "departure")}
            style={{ marginBottom: 20 }}
            min={0}
            step={0.5}
            allowCross={false}
            max={MAX_HOURS}
            value={departureValue}
            onAfterChange={(val) => setFilterTimeQuery(val, "departure")}
          />
          <FilterRangeDescription
            translationId="Filters.returnTime"
            rangeId={`departure-time-label-${activeFilter}`}
          >
            {labelFormat(arrivalLabel, hourMinuteFormat(defaultCountry))}
          </FilterRangeDescription>
          <Range
            tipFormatter={(value) =>
              moment(rangeNumToTime(value), "HH:mm").format(
                hourMinuteFormat(defaultCountry)
              )
            }
            onChange={(e) => setFilterTime(e, "arrival")}
            style={{ marginBottom: 20 }}
            min={0}
            step={0.5}
            allowCross={false}
            max={MAX_HOURS}
            value={arrivalValue}
            onAfterChange={(val) => setFilterTimeQuery(val, "arrival")}
          />

          <FilterRangeDescription
            translationId="Filters.transferDuration"
            rangeId={`transfer-time-label-${activeFilter}`}
          >
            {transferLabel}
          </FilterRangeDescription>
          <Range
            tipFormatter={(value) => rangeNumToDuration(value)}
            onChange={(e) => setFilterTime(e, "transfer")}
            style={{ marginBottom: 20 }}
            min={0}
            step={0.5}
            allowCross={false}
            max={MAX_HOURS}
            value={transferValue}
            onAfterChange={(val) => setFilterTimeQuery(val, "transfer")}
          />
          <FilterRangeDescription
            translationId="Filters.journeyDuration"
            rangeId={`journey-time-label-${activeFilter}`}
          >
            {jurneyDurationLabel}
          </FilterRangeDescription>
          <Range
            tipFormatter={(value) => rangeNumToDuration(value)}
            onChange={(e) => setFilterTime(e, "journeyDuration")}
            style={{ marginBottom: 20 }}
            min={0}
            step={0.5}
            allowCross={false}
            max={timeCellingHours}
            value={journeyDurationValue}
            onAfterChange={(val) => setFilterTimeQuery(val, "journeyDuration")}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterBox2;
